/**
 * @lines98/client/services/api-client — fetch wrapper for HTTP к server.
 *
 * Single source of truth для server API calls. Per ADR-0006 §D2 Bearer transport:
 *   - Reads current secretToken from identity service (lazy import — circular
 *     dep avoidance: api-client → identity → boot via api-client.post for
 *     POST /auth/anonymous).
 *   - Attaches `Authorization: Bearer <secretToken>` header автоматически если
 *     identity exists. Endpoints без identity (e.g., POST /auth/anonymous) пройдут
 *     без header.
 *   - JSON marshals body; parses response (text fallback на non-JSON).
 *
 * Configuration:
 *   - Base URL = `import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'`
 *     (Vite convention). Override через `.env.local` rooted at packages/client/.
 *
 * Error handling:
 *   - Network errors (fetch reject) → thrown ApiClientError с `kind: 'network'`.
 *   - HTTP non-2xx → returned `{ ok: false, status, body }` (NOT thrown — callers
 *     branch on status; matches dwarves' pattern of typed response envelopes).
 *   - 2xx с invalid JSON body → thrown ApiClientError с `kind: 'parse'`.
 *
 * Per ADR-0001 §A2: no axios / no heavy abstraction. fetch + types only.
 *
 * HTTP-cache policy (brief-44 §B / instant-ui-hydration §3.1): every request runs
 * with `cache: 'no-store'` — the API serves server-authoritative live data, so a
 * background revalidation GET must actually hit the server, never the browser HTTP
 * cache (else stale-while-revalidate reconcile sees the same cached bytes and never
 * surfaces a new result). One funnel, no per-route `Cache-Control` duplication.
 *
 * Stale-identity auto-recovery (incident 2026-06-06):
 *   - After the dev-DB was recreated, a player's old IndexedDB identity no longer
 *     resolved on the server: auth-plugin hashed the saved Bearer, found no
 *     `players` row, and answered 401 `{ reason: 'token_not_recognized' }`
 *     forever. The stale identity 401'd on every authed request — normal modes
 *     dead-ended ("Не удалось начать игру"), only the public daily board worked.
 *   - Fix lives HERE, in `request()` — the single funnel every authed call passes
 *     through (Rule §1 «ствол → ветки»: one source of truth, no per-screen copies).
 *     On 401 with reason `token_not_recognized` AND ONLY that reason, the saved
 *     identity is treated as stale: clear() it, re-mint anonymous via
 *     bootIdentity() (storage is now empty → server mints fresh), and replay the
 *     ORIGINAL request EXACTLY ONCE with the new Bearer.
 *   - Anti-cheat is intact: this re-issues an ANONYMOUS identity (equivalent to a
 *     fresh browser), it is NOT an auth bypass and grants NO elevated rights — the
 *     replay still passes through the same server auth + replayScore checks. See
 *     incident 2026-06-06 note above + ADR-0006 §D2.
 *
 * Revoked-session detection (incident 2026-06-10):
 *   - The SAME 401 `token_not_recognized` arises for a second, different cause: an
 *     AUTHED identity whose account-shared `secret_token_hash` was rotated by a
 *     login in ANOTHER browser/device. The silent re-mint above is WRONG here — it
 *     would lose the account AND boot a fresh anonymous guest that could submit the
 *     ranked daily under a ghost identity (the orphan-attempt incident).
 *   - Fork in `request()`: on a stale-token 401 we branch on `isAuthed()`. AUTHED
 *     → `emitSessionRevoked()` (app-shell shows an explicit banner + does cleanup
 *     on the player's choice) and return the 401 unchanged — NO background re-mint,
 *     NO ranked side effect. ANONYMOUS → the existing re-mint+retry path.
 *
 * Cross-references:
 *   - ADR-0006 §D2 (Bearer transport + secretToken shape; 401 reason contract).
 *   - ADR-0006 §D1 (anonymous mint — first call has no Bearer).
 *   - services/identity.ts (Bearer source + clear/bootIdentity for re-mint; isAuthed).
 *   - lib/session-revoked.ts (revoked-session signal — emitSessionRevoked).
 *   - packages/server/src/plugins/auth.ts (401 `token_not_recognized` origin — step 4).
 *   - services/oauth-login.ts (consumer).
 */

// Static import is safe despite the identity → api-client cycle: every use here
// is deferred to call-time (request()), never evaluated at module-init, so ESM
// resolves the cycle cleanly (same reason getCurrentBearer already lives here).
import { getCurrentBearer, clear as clearIdentity, bootIdentity, isAuthed } from './identity.js';
import { emitSessionRevoked } from '../lib/session-revoked.js';

// =============================================================================
// Types
// =============================================================================

export interface ApiSuccess<T> {
  readonly ok: true;
  readonly status: number;
  readonly body: T;
}

export interface ApiFailure {
  readonly ok: false;
  readonly status: number;
  /**
   * Server error body (shape: `{ ok: false, kind, reason }` per server convention).
   * Best-effort JSON parse; raw text if non-JSON. Never null — empty body
   * surfaces as empty object.
   */
  readonly body: unknown;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export class ApiClientError extends Error {
  readonly kind: 'network' | 'parse';
  constructor(kind: 'network' | 'parse', message: string, options?: ErrorOptions) {
    super(message, options);
    this.kind = kind;
    this.name = 'ApiClientError';
  }
}

// =============================================================================
// Base URL resolution
// =============================================================================

/**
 * Resolve server base URL from Vite env. Falls back к localhost:3000 for dev.
 * Exposed as a function (not a constant) so tests can stub the env-read
 * by patching `import.meta.env` before module load.
 */
function getBaseUrl(): string {
  // import.meta.env is statically replaced by Vite at build-time; в tests
  // (Node vitest без Vite plugin) it's undefined → fallback wins.
  if (typeof import.meta === 'undefined') return 'http://localhost:3000';
  const env = (import.meta as unknown as {
    env?: Record<string, string | undefined>;
  }).env;
  const fromEnv = env?.['VITE_API_BASE_URL'];
  return fromEnv ?? 'http://localhost:3000';
}

// =============================================================================
// Internal: build headers (Bearer injection)
// =============================================================================

/**
 * Build request headers. Includes `Authorization: Bearer <token>` если identity
 * есть; иначе omits the header (анонимные endpoints like POST /auth/anonymous
 * не требуют Bearer).
 *
 * `hasBody` (default true) — будет ли у запроса JSON-тело. `Content-Type:
 * application/json` ставится ТОЛЬКО когда тело реально есть. ЗАЧЕМ (incident
 * 2026-06-25, «удаление чужого реплея в Архиве не работало»): body-less `DELETE`
 * `/book/others/bookmark/:short_id` уходил с `Content-Type: application/json` и
 * ПУСТЫМ телом → Fastify content-type-парсер отбивал его `400
 * FST_ERR_CTP_EMPTY_JSON_BODY` («Body cannot be empty when content-type is set to
 * application/json») ДО хэндлера. Клиент видел `!res.ok` → `doRemove` молча выходил,
 * строка не удалялась. Объявлять `Content-Type` тела на запросе БЕЗ тела —
 * некорректно по HTTP; не ставим заголовок → Fastify не пытается парсить пустое
 * тело, DELETE доходит до хэндлера и удаляет закладку. (curl «работал» именно
 * потому, что не слал этот заголовок.) GET тоже body-less → тот же путь.
 *
 * Exported for unit tests (C-AC-1).
 */
export function buildHeaders(extras?: HeadersInit, hasBody = true): Headers {
  const headers = new Headers(extras);
  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  const bearer = getCurrentBearer();
  if (bearer && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${bearer}`);
  }
  return headers;
}

// =============================================================================
// Stale-identity recovery (incident 2026-06-06)
// =============================================================================

/**
 * The ONLY 401 reason that means "your saved identity is stale, re-mint and
 * retry". Other 401 reasons (missing_authorization_header /
 * invalid_authorization_scheme / invalid_token_format) describe a malformed
 * *current* request, not a token the server has simply forgotten — re-minting
 * would not fix them and could mask a client bug, so we MUST NOT recover on them.
 * Sourced verbatim from packages/server/src/plugins/auth.ts step 4 (`if (!row)`).
 */
const RECOVERABLE_AUTH_REASON = 'token_not_recognized';

/** The anonymous-mint endpoint itself — never auto-recover here (no Bearer is
 *  attached, and re-minting from within its own failure path is the recursion
 *  the one-retry guard exists to forbid). */
const ANONYMOUS_MINT_PATH = '/auth/anonymous';

/**
 * Does this failure envelope describe a stale-identity 401 we should recover
 * from? True only for HTTP 401 whose body carries `reason: 'token_not_recognized'`.
 */
function isStaleIdentity401(status: number, body: unknown): boolean {
  if (status !== 401) return false;
  if (!body || typeof body !== 'object') return false;
  return (body as { reason?: unknown }).reason === RECOVERABLE_AUTH_REASON;
}

// =============================================================================
// Core request
// =============================================================================

interface RequestOptions {
  /** Additional headers (merge); Authorization overridden by Bearer if set. */
  headers?: HeadersInit;
  /** Optional override for base URL (tests). */
  baseUrl?: string;
  /**
   * Internal recursion guard. When `request()` re-fires itself after re-minting
   * a stale identity, it sets this true so the retry can NEVER itself trigger a
   * second recovery — at most ONE retry per call, no infinite loop. NOT part of
   * the public `apiClient.get/post` surface (callers never set it).
   */
  _isRetryAfterReMint?: boolean;
}

async function request<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body: unknown,
  options?: RequestOptions,
): Promise<ApiResponse<T>> {
  const base = options?.baseUrl ?? getBaseUrl();
  const url = `${base}${path}`;

  // Capture whether a Bearer is attached BEFORE the request — recovery only
  // makes sense when we actually sent a saved identity that the server rejected.
  // A request without identity (e.g. /auth/anonymous, or a public read by a
  // brand-new visitor) has nothing stale to re-mint.
  const hadBearer = getCurrentBearer() !== null;

  // Будет ли JSON-тело: только для не-GET с непустым body. `Content-Type:
  // application/json` ставим лишь когда тело реально есть (incident 2026-06-25 —
  // body-less DELETE с json-content-type → Fastify 400 FST_ERR_CTP_EMPTY_JSON_BODY;
  // см. buildHeaders). Та же проверка, что для `init.body` ниже — один источник правды.
  const hasBody = body !== undefined && body !== null && method !== 'GET';

  const init: RequestInit = {
    method,
    headers: buildHeaders(options?.headers, hasBody),
    // Bypass the browser HTTP cache for EVERY API call (single funnel — Rule §1
    // «ствол → ветки»: one place, no per-route Cache-Control duplication). Our API
    // is server-authoritative live data (leaderboards, meta-state, daily seed); a
    // 200 served from the browser HTTP cache would defeat stale-while-revalidate —
    // a background revalidation GET could return the SAME cached bytes, so the
    // leaderboard reconcile would see «те же данные» and never surface a new result
    // until a hard reload (brief-44 §B AC-5 / instant-ui-hydration §3.1 root cause 2).
    // `no-store` forces the request to actually reach the server. Harmless for POSTs
    // (never cached anyway). Server stays the single source of truth (anti-cheat intact).
    cache: 'no-store',
  };
  if (hasBody) {
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    throw new ApiClientError(
      'network',
      `fetch failed: ${(err as Error).message}`,
      { cause: err },
    );
  }

  // Parse response body — JSON if possible, else raw text wrapped.
  const rawText = await response.text();
  let parsedBody: unknown;
  if (rawText.length === 0) {
    parsedBody = {};
  } else {
    try {
      parsedBody = JSON.parse(rawText);
    } catch {
      // Server returned non-JSON (unexpected for our API); preserve as text.
      parsedBody = { rawText };
    }
  }

  if (response.status >= 200 && response.status < 300) {
    return { ok: true, status: response.status, body: parsedBody as T };
  }

  // ── 401 token_not_recognized: revoked-session vs stale-anon fork ─────────────
  // A `token_not_recognized` on a Bearer we DID send means the server no longer
  // knows this token. There are TWO distinct causes — they must NOT be handled
  // the same way (root of incident 2026-06-10):
  //
  //   (A) The identity was AUTHED (вошёл через email/Google/Discord) → another
  //       browser/device logged into the SAME account and rotated the shared
  //       `players.secret_token_hash`, invalidating this browser's token. This is
  //       a REVOKED SESSION. A silent re-mint here would (1) lose the player's
  //       account silently AND (2) boot a fresh ANONYMOUS guest that could
  //       immediately play/submit the ranked daily board under a ghost identity
  //       (the orphan-attempt incident). So: do NOT re-mint. Emit the revoked
  //       signal (app-shell shows the explicit banner + does the cleanup on the
  //       player's choice) and return the 401 unchanged — the in-flight request
  //       fails cleanly, no ranked side effect.
  //
  //   (B) The identity was ANONYMOUS → either the dev-DB was recreated (incident
  //       2026-06-06) or a stale local mint. Re-issuing a fresh anonymous identity
  //       (equivalent to a brand-new browser) is correct and is NOT an auth bypass
  //       — see file header. Keep the EXACTLY-ONCE re-mint + retry.
  const staleToken401 =
    !options?._isRetryAfterReMint &&
    hadBearer &&
    path !== ANONYMOUS_MINT_PATH &&
    isStaleIdentity401(response.status, parsedBody);

  if (staleToken401) {
    if (isAuthed()) {
      // (A) Revoked session — explicit banner, NO silent guest re-mint.
      // emitSessionRevoked de-dups across the burst of parallel 401s. We do NOT
      // clear identity here: the banner owns cleanup, so it can read the (still
      // authed) identity to phrase the message before wiping it.
      emitSessionRevoked();
      return { ok: false, status: response.status, body: parsedBody };
    }
    // (B) Stale anonymous — wipe the rejected identity (also resets
    // bootIdentity's in-flight promise), re-mint anonymous against the now-empty
    // storage, retry once. If the re-mint fails (server down / 5xx), fall through
    // and return the original 401 unchanged.
    await clearIdentity();
    const reminted = await bootIdentity();
    if (reminted) {
      // Replay the original request with the fresh Bearer. `_isRetryAfterReMint`
      // hard-caps recursion at one retry: a second 401 returns as-is below.
      return request<T>(method, path, body, {
        ...options,
        _isRetryAfterReMint: true,
      });
    }
  }

  return { ok: false, status: response.status, body: parsedBody };
}

// =============================================================================
// Public API
// =============================================================================

export const apiClient = {
  /** POST <path> с JSON body. Returns typed response envelope. */
  post<T>(
    path: string,
    body: unknown = {},
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return request<T>('POST', path, body, options);
  },

  /** GET <path>. Returns typed response envelope. */
  get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('GET', path, undefined, options);
  },

  /**
   * DELETE <path>. Returns typed response envelope (same funnel — Bearer / no-store /
   * stale-identity recovery apply identically). Added for the «Книга» bookmark removal
   * (`DELETE /book/others/bookmark/:short_id`, ADR-0023) — server contract is DELETE, not
   * a POST-эмуляция; routed through the single `request` ствол (no per-screen fetch).
   */
  del<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('DELETE', path, undefined, options);
  },

  /** Expose base URL resolver для consumers that need it (e.g., OAuth init log). */
  baseUrl(): string {
    return getBaseUrl();
  },
} as const;
